/**
 * Why a progression rule cannot be authored — every refusal the backend makes at save, made here on
 * the way IN instead, so a plan never looks saved and then 400s.
 *
 * Each is stated as a REASON rather than a sentence. `model/` holds no prose; whichever surface
 * shows the refusal words it, and a refusal it can name is what lets a control be stated rather than
 * hidden. A hidden option and a dead option teach the trainer the same wrong thing — that the app
 * has no opinion. RPE is the case that made it necessary: a perfectly ordinary column the movement
 * measures, and the only reason it cannot be a gate is that the dictionary says it records how work
 * FELT rather than what was set.
 *
 * ── Ported from `move-with-me-frontend/.../plans/builder/model/bandedField.ts` ────────────────
 * 1:1, plus the `RuleRefusal` shape `draftToRequest` already used for a serialisation refusal — the
 * two moments answer the same question and a trainer told one thing at the control and another at
 * the save is being told two things about one rule.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`.
 */

import { measurementFieldDefinition, type MeasurementFieldKey } from '~/shared/measurement';

import type { ProgressionOverride, SetDraft } from './planDraft.types';
import { isPercentMode } from './prescriptionMode';
import {
  ruleFor,
  ruledFields,
  type ProgressionRule,
  type ProgressionRules,
} from './progressionRules';
import { derivesLoadInSession } from './setShape';

/** A refusal, ready to be worded by whichever surface shows it. */
export interface RuleRefusal {
  /** The i18n key, always in the `web:` namespace. */
  readonly key: string;
  /** Interpolation values the key needs. Empty for the keys that name no number. */
  readonly values: Readonly<Record<string, string | number>>;
}

/**
 * The column the classic reading bands — "climb to the top of the rep range and the load goes up".
 * R3 names it as the authoring surface's preset, so it is preferred wherever the exercise measures
 * it; anything else is a deliberate choice the trainer makes rather than a default worth guessing at.
 */
const PRESET_GATED_FIELD: MeasurementFieldKey = 'reps';

/** The value types that carry no bar to clear, so no band can sit on them. */
const NON_NUMERIC_VALUE_TYPES: ReadonlySet<string> = new Set(['TextNotation', 'ChoiceFromList']);

/** The column whose prescription becomes a fraction of the client's max — the one % mode re-denominates. */
const LOAD_FIELD: MeasurementFieldKey = 'weight';

/** What deciding a banded column needs: the exercise's contract, and where its rules already sit. */
export interface GatedFieldContext {
  /** The columns the movement measures, in the dictionary's canonical order. */
  readonly measurementFields: readonly MeasurementFieldKey[];
  /** The exercise's rules — a column already carrying one is offered, and the band REPLACES that rule. */
  readonly progressionRules: ProgressionRules;
  /**
   * How the LOAD is priced. Optional because a caller holding a rule map and a contract and nothing
   * else does not ask about the load column.
   */
  readonly prescriptionMode?: string;
  /**
   * The exercise's own factor of the client's max, or null where it states none. Optional for the
   * same reason `prescriptionMode` is: a caller asking only about bands holds no exercise.
   */
  readonly realMaxMultiplier?: number | null;
  /**
   * The exercise's rows and the loads pinned onto them, for the one refusal that is stated in terms
   * of VALUES rather than of a flag (CSL R7). Optional for the same reason the two above are.
   */
  readonly sets?: readonly SetDraft[];
  readonly progressionOverrides?: readonly ProgressionOverride[];
}

/**
 * The whole of the client's max, as a factor. The top of the per-set FRACTION axis, and therefore
 * the line the refusal below is drawn at — `ProgressionCalculator.PercentCeiling` on the backend.
 */
const WHOLE_MAX_MULTIPLIER = 1;

/**
 * Why this column's rule may not state a per-cycle step, or null when it may.
 *
 * `noBaseToExpand` — CSL R7. A static weight delta needs a base to expand at AUTHORING time, and an
 *   exercise pricing no load at all gives it none: the calculator tolerates the null base and
 *   silently produces a flat plan, so the trainer gets a 200, a rule on the card and no progression
 *   anywhere. The backend refuses it
 *   (`Services/StaticProgressionModule.cs` `RequireABaseTheDeltaCanExpand`). What the trainer wants
 *   there is the execution-gated rule, which chains off the client's own first performance — which
 *   is what "+2,5 kg a cycle" MEANS on an exercise whose load the client picks — and the sheet
 *   already offers it beside the refused entry.
 *
 *   R7 is stated in terms of VALUES rather than of a flag, deliberately and in its own words:
 *   `ClientSelected` left `PrescriptionMode` when an empty weight cell became the carrier of that
 *   state, and a guard reading the retired mode key would be a guard reading nothing.
 *
 * `multiplierAboveWholeMax` — PP R4. An exercise whose multiplier prices its unpriced rows ABOVE the
 *   client's whole max may not also ask that price to advance. The two instructions have no common
 *   reading: the multiplier is a FACTOR and resolves at its full value at workout start, while a
 *   delta expands the per-set FRACTION, whose top IS the whole max. Authored together, the client
 *   would be handed the whole max flat for every cycle of the phase while the plan claimed to climb
 *   from 120 %. The backend refuses the pair at save
 *   (`Services/StaticProgressionModule.cs` `RefuseADeltaUnderAMultiplierAboveTheWholeMax`); without
 *   this the trainer authors it, presses save and reads a 400 about a rule they cannot see.
 *
 * It holds on the LOAD alone and on a STATIC rule alone, exactly as the backend's guard does: a
 * fraction of the max is a way of writing a LOAD, and an execution-gated rule chains off the
 * client's own logged kilograms rather than expanding the stored fraction, so neither meets the
 * contradiction.
 *
 * A NULL `rule` asks it of a Static rule about to be written — which is what the family control
 * needs, since picking „Manualna" seeds the column's opening step and a flat static rule carries no
 * progression to draw a stepper on.
 */
export type DeltaRefusal = 'multiplierAboveWholeMax' | 'noBaseToExpand';

export function deltaRefusal(
  context: GatedFieldContext,
  field: MeasurementFieldKey,
  rule: ProgressionRule | null,
): DeltaRefusal | null {
  if (field !== LOAD_FIELD) return null;
  if (rule !== null && rule.type !== 'Static') return null;
  if (!hasPrescribedLoad(context)) return 'noBaseToExpand';
  const multiplier = context.realMaxMultiplier;
  // Null and undefined both mean "this exercise states no factor", and neither is a factor of one.
  if (multiplier === null || multiplier === undefined) return null;
  return multiplier > WHOLE_MAX_MULTIPLIER ? 'multiplierAboveWholeMax' : null;
}

/**
 * Whether the trainer priced a load AT ALL — in kilograms, as a per-set fraction of the client's
 * max, or as the exercise-wide multiplier of it. The backend's `HasPrescribedLoad`, read the same
 * three ways for the same reason (R4: a multiplier prices every unpriced row exactly as a per-set
 * percent prices one, so reading the sets alone refuses a whole legitimate mode).
 *
 * TWO ROW KINDS ARE NOT A BASE, and both exclusions are load-bearing. A warm-up ramp rides flat
 * across cycles, and a ladder member's percent is a fraction of the TOP SET's actual weight rather
 * than of the max — so neither is something a static rule can expand, and counting either would let
 * the refused shape through on an exercise whose only priced rows are a warm-up and a back-off.
 *
 * A per-cycle PIN counts. A trainer who left cycle 1 open and typed a load into cycle 3 has priced
 * the exercise, the backend reads it as a base, and refusing it here would be a refusal the save
 * does not make — which is worse than the gap, because a control that refuses what the server
 * accepts teaches the trainer to distrust it.
 */
function hasPrescribedLoad(context: GatedFieldContext): boolean {
  if (context.realMaxMultiplier !== null && context.realMaxMultiplier !== undefined) return true;
  const sets = context.sets ?? [];
  const pinned = context.progressionOverrides ?? [];
  return sets.some((set, index) => {
    if (set.setType === 'Warmup' || derivesLoadInSession(set)) return false;
    if (set.prescribedWeight !== null || set.prescribedPercent !== null) return true;
    return pinned.some(
      (override) =>
        override.setIndex === index + 1 &&
        (override.prescribedWeight !== null || override.prescribedPercent !== null),
    );
  });
}

/**
 * WHY a column may not carry the band.
 *
 * `thisJumps` — it is the column the rule advances, the mock's "to skacze".
 * `notDeciding` — the dictionary says it does not decide progression, or holds it as a notation with
 *   no bar to clear.
 * `notMeasured` — the movement does not measure it (R9), including the case where it does not
 *   measure the STEPPED column either.
 * `pricedInPercent` — the load, on an exercise priced in %1RM. The rows carry no kilograms there at
 *   all, so a band on the load column is a bar written in a unit the prescription does not use: the
 *   sheet would seed kilograms onto percent rows. The load is not a thing the client CLIMBS in that
 *   mode; it is a thing the max moves.
 */
export type GateRefusal = 'thisJumps' | 'notDeciding' | 'notMeasured' | 'pricedInPercent';

/** Why `candidate` may not be gated on by a rule keyed under `steppedField`, or null when it may. */
export function gateRefusal(
  context: GatedFieldContext,
  steppedField: MeasurementFieldKey,
  candidate: MeasurementFieldKey,
): GateRefusal | null {
  // The STEPPED column is measured too. R9 is a ceiling on every column a rule names, and the one it
  // WRITES is the first of them: the backend deliberately does not gate it at save and then SKIPS
  // the rule at workout start, so a rule keyed on a column the movement does not measure is
  // accepted, does nothing, and leaves the card's row promising a progression forever.
  if (!context.measurementFields.includes(steppedField)) return 'notMeasured';
  if (candidate === steppedField) return 'thisJumps';
  if (!context.measurementFields.includes(candidate)) return 'notMeasured';
  // A column carrying a rule of its own is NOT refused — it is offered, and binding the band to it
  // REPLACES that rule in one act, after the trainer has been asked. Refusing it outright left a
  // weight+reps movement with no way to progress reps, because the reps column was silently holding
  // a band nobody had been shown.
  if (candidate === LOAD_FIELD && isPercentMode(context.prescriptionMode ?? '')) {
    return 'pricedInPercent';
  }

  const definition = measurementFieldDefinition(candidate);
  if (NON_NUMERIC_VALUE_TYPES.has(definition.valueType)) return 'notDeciding';
  return definition.decidesProgression ? null : 'notDeciding';
}

/** Whether `candidate` may carry the band of a rule keyed under `steppedField`. Total. */
export function isEligibleGatedField(
  context: GatedFieldContext,
  steppedField: MeasurementFieldKey,
  candidate: MeasurementFieldKey,
): boolean {
  return gateRefusal(context, steppedField, candidate) === null;
}

/**
 * Why this column may not be given a rule of its own, or null when it may.
 *
 * `gatesAnotherRule` IS THE HARD WALL, and it is deliberately asymmetric with `gateRefusal`, which
 * offers a band over a column that already carries a rule and asks. The two directions destroy
 * different amounts: removing a rule from the banded column throws away one thing the trainer typed,
 * so it gets a prompt; removing a BAND to make room for a rule silently reshapes what the other
 * column's rule means, so it gets a wall — and the wall names its exit, which is turning the range
 * off in the other column.
 *
 * It fires ONLY where a band is actually on: `rule.banded` is null on every unbanded execution-gated
 * rule, so such a rule claims no column and blocks none.
 */
export type ColumnRuleRefusal = 'notMeasured' | 'gatesAnotherRule';

export function columnRuleRefusal(
  context: GatedFieldContext,
  field: MeasurementFieldKey,
): ColumnRuleRefusal | null {
  if (!context.measurementFields.includes(field)) return 'notMeasured';
  return columnBandedBy(context.progressionRules, field) === null ? null : 'gatesAnotherRule';
}

/**
 * Which column's rule places its band on `field`, or null when none does.
 *
 * The refusal above and the sentence stating its WAY OUT are one answer read twice — "you cannot
 * rule this column" and "turn the range off over there" — so they come from one walk. Deriving the
 * exit from `bandOf` instead pointed at nothing exactly when the trainer had erased the range but
 * not the band.
 */
export function columnBandedBy(
  rules: ProgressionRules,
  field: MeasurementFieldKey,
): MeasurementFieldKey | null {
  return (
    ruledFields(rules).find((ruled) => {
      const rule = ruleFor(rules, ruled);
      return rule !== null && rule.type === 'PreviousExecution' && rule.banded === field;
    }) ?? null
  );
}

/**
 * Why this column's rule may not read „od wyniku", or null when it may.
 *
 * ONE refusal, and it is the wire's shape rather than a preference: an exercise carries AT MOST ONE
 * execution-gated rule, because a band's ceilings are one number per SET and nothing on them says
 * which rule they belong to, so a second such rule would need a second ceiling per row the wire has
 * no room for. `bandOf` already assumes it — it answers null on two, which reads as "no band" and
 * quietly hides ceilings the trainer authored. The backend states it too
 * (`RequireSingleDynamicRule`).
 *
 * A movement measuring ONE column is not refused: a rule needs no band, so it advances on the
 * delivered set. Refusing the whole family there would deny the commonest exercise in the app the
 * commonest rule in the app.
 */
export type FromResultRefusal = 'otherRuleFromResult';

export function fromResultRefusal(
  context: GatedFieldContext,
  steppedField: MeasurementFieldKey,
): FromResultRefusal | null {
  // The column being authored does not count against itself: re-entering the family on a rule that
  // already reads this way is the commonest reason to open the control at all.
  const elsewhere = ruledFields(context.progressionRules).some(
    (field) =>
      field !== steppedField &&
      ruleFor(context.progressionRules, field)?.type === 'PreviousExecution',
  );
  return elsewhere ? 'otherRuleFromResult' : null;
}

/**
 * The column to PRE-HIGHLIGHT when the trainer turns the range on, or null when this exercise has
 * none — a movement measuring a single column, or one that does not measure `steppedField` at all.
 *
 * Null refuses the range SWITCH, never the family: a rule with no band is a complete rule.
 *
 * A FREE COLUMN WINS OVER THE PRESET. Every eligible column is offered — one carrying a rule of its
 * own is a price rather than a wall — but a DEFAULT landing on a ruled column turns opening the
 * picker into a demand for that rule's life, on a control whose own note promises the trainer they
 * are about to choose. So the preset is taken while it is free, then the first free column, and a
 * ruled one only when nothing free is left.
 */
export function defaultGatedField(
  context: GatedFieldContext,
  steppedField: MeasurementFieldKey,
): MeasurementFieldKey | null {
  const eligible = context.measurementFields.filter((field) =>
    isEligibleGatedField(context, steppedField, field),
  );
  const free = eligible.filter((field) => ruleFor(context.progressionRules, field) === null);
  if (free.includes(PRESET_GATED_FIELD)) return PRESET_GATED_FIELD;
  if (free.length > 0) return free[0] ?? null;
  if (eligible.includes(PRESET_GATED_FIELD)) return PRESET_GATED_FIELD;
  return eligible[0] ?? null;
}
